import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { useSeo } from "@/hooks/useSeo";
import { ARTICLES } from "@/data/blogArticles";

const SERVICES = [
  { to: "/permis", label: "Permis B" },
  { to: "/permis-accelere", label: "Permis accéléré" },
  { to: "/transfert-auto-ecole", label: "Transfert d'auto-école" },
];

function BlogHeader() {
  return (
    <header className="flex items-center justify-between border-b border-neutral-200 px-6 py-fib-4 sm:px-12">
      <Link to="/" data-testid="blog-home-link">
        <Logo className="h-10" />
      </Link>
      <div className="flex items-center gap-fib-4 text-gr-sm font-semibold">
        <Link to="/blog" className="hidden text-brand sm:inline">Blog</Link>
        <Link to="/ambassadeur" className="hidden text-neutral-600 hover:text-brand sm:inline">Devenir ambassadeur</Link>
        <Link to="/" className="rounded-full bg-brand px-fib-4 py-fib-2 text-white hover:opacity-90">Faire ma demande</Link>
      </div>
    </header>
  );
}

export default function Blog() {
  useSeo({
    title: "Blog PlanPermis — Guides & conseils pour le permis de conduire",
    description:
      "Guides pratiques 2026 sur le permis de conduire : financement, CPF, budget et aides. Des conseils clairs pour préparer votre permis et trouver une auto-école.",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Blog PlanPermis",
      description: "Guides et conseils pour le permis de conduire en France.",
    },
  });

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <BlogHeader />

      <section className="px-6 py-fib-7 sm:px-12">
        <div className="mx-auto max-w-6xl">
          <p className="text-gr-xs font-bold uppercase tracking-[0.2em] text-brand">Le blog</p>
          <h1 className="mt-fib-3 text-gr-2xl font-extrabold tracking-tight text-neutral-900 sm:text-gr-3xl text-balance">
            Guides & conseils pour votre permis
          </h1>
          <p className="mt-fib-4 measure-wide text-gr-lg text-neutral-600">
            Financement, budget, CPF, aides régionales : nos guides 2026 vous aident à y voir clair et à obtenir votre
            permis plus vite, au meilleur coût.
          </p>

          <div className="mt-fib-6 grid gap-fib-5 md:grid-cols-2 lg:grid-cols-3">
            {ARTICLES.map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                data-testid={`blog-card-${a.slug}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white transition-shadow hover:shadow-lg"
              >
                <div className="aspect-[16/9] w-full overflow-hidden bg-gradient-to-br from-brand to-brand-active">
                  {a.image ? (
                    <img src={a.image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center p-fib-5">
                      <span className="font-display text-gr-xl font-black text-white/90">Plan<span className="text-white/60">Permis</span></span>
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-fib-5">
                  <div className="flex items-center gap-fib-3 text-gr-xs font-semibold text-neutral-400">
                    <span className="rounded-full bg-brand/10 px-fib-3 py-fib-1 text-brand">{a.category}</span>
                    <span>{a.readingTime} min de lecture</span>
                  </div>
                  <h2 className="mt-fib-3 text-gr-lg font-bold text-neutral-900 group-hover:text-brand">{a.title}</h2>
                  <p className="mt-fib-2 flex-1 text-gr-sm text-neutral-600">{a.excerpt}</p>
                  <span className="mt-fib-4 inline-flex items-center gap-fib-2 text-gr-sm font-bold text-brand">
                    Lire l'article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-fib-7 rounded-2xl border border-brand/30 bg-brand/5 p-fib-6">
            <p className="text-gr-lg font-bold text-neutral-900">Prêt(e) à passer à l'action ?</p>
            <p className="mt-fib-2 text-gr-sm text-neutral-600">
              Une fois votre budget calé, trouvez une auto-école partenaire avec des dates d'examen disponibles.
            </p>
            <div className="mt-fib-4 flex flex-wrap items-center gap-fib-3">
              <Link to="/" data-testid="blog-cta" className="inline-flex items-center gap-fib-2 rounded-full bg-brand px-fib-6 py-fib-3 text-gr-base font-bold text-white hover:opacity-90">
                Faire ma demande <ArrowRight className="h-4 w-4" />
              </Link>
              {SERVICES.map((s) => (
                <Link key={s.to} to={s.to} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-fib-4 py-fib-2 text-gr-sm font-semibold text-neutral-700 transition-colors hover:border-brand hover:text-brand">
                  {s.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
